import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import Card from '../components/Card';
import NavBar from '../components/NavBar';
import EducationalBanner from '../components/EducationalBanner';
import { useFirstVisit } from '../hooks/useFirstVisit';

function SleepSense() {
  const navigate = useNavigate();
  const [alarmTime, setAlarmTime] = useState('07:00');
  const [smartWindow, setSmartWindow] = useState(30);
  const [alarmSet, setAlarmSet] = useState(false);
  const { isFirstVisit, setVisited } = useFirstVisit('sleep');
  const [showEducationalBanner, setShowEducationalBanner] = useState(false);

  // Scroll to top on mount and check for first visit
  useEffect(() => {
    window.scrollTo(0, 0);
    
    if (isFirstVisit) {
      setShowEducationalBanner(true);
    }
  }, [isFirstVisit]);

  function setAlarm() {
    setAlarmSet(true);
    setTimeout(() => setAlarmSet(false), 3000);
  }

  // Handle closing the educational banner
  const handleCloseBanner = () => {
    setShowEducationalBanner(false);
    setVisited();
  };

  return (
    <div style={{ maxWidth: '480px', margin: 'auto', padding: '20px', backgroundColor: '#f5f7fa', minHeight: '100vh', paddingTop: '60px' }}>
      <NavBar title="😴 SleepSense Tracker" />
      
      {/* Educational Banner - Only shows on first visit */}
      {showEducationalBanner && (
        <EducationalBanner 
          feature="sleep" 
          onDismiss={handleCloseBanner} 
        />
      )}
      
      <Card style={{ textAlign: 'center' }}>
        <h1 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '700' }}>😴 SleepSense Tracker</h1>
        <p style={{ color: '#64748b', margin: '0 0 16px 0' }}>
          Track your sleep patterns and wake up during light sleep
        </p>
      </Card>

      <Card>
        <h3 style={{ color: '#2c3e50', marginBottom: '16px', fontWeight: '600' }}>Last Night&apos;s Sleep</h3>
        
        {/* Mock Sleep Chart */}
        <div style={{ 
          height: '120px', 
          backgroundColor: '#f8f9fa', 
          borderRadius: '8px', 
          padding: '16px',
          marginBottom: '16px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>Sleep Stages</div>
          
          {/* Mock sleep stage visualization */}
          <div style={{ display: 'flex', alignItems: 'end', height: '60px', gap: '2px' }}>
            {[20, 40, 60, 80, 45, 30, 70, 90, 50, 35, 25, 15].map((height, i) => (
              <div key={i} style={{
                backgroundColor: height > 60 ? '#4ade80' : height > 40 ? '#4361ee' : '#facc15',
                height: `${height}%`,
                flex: 1,
                borderRadius: '2px 2px 0 0'
              }} />
            ))}
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#64748b', marginTop: '4px' }}>
            <span>11 PM</span>
            <span>3 AM</span>
            <span>7 AM</span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', textAlign: 'center' }}>
          <div>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#4ade80' }}>7h 42m</div>
            <div style={{ fontSize: '12px', color: '#64748b' }}>Total Sleep</div>
          </div>
          <div>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#4361ee' }}>85%</div>
            <div style={{ fontSize: '12px', color: '#64748b' }}>Efficiency</div>
          </div>
          <div>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#facc15' }}>3</div>
            <div style={{ fontSize: '12px', color: '#64748b' }}>Awakenings</div>
          </div>
        </div>
      </Card>

      <Card>
        <h3 style={{ color: '#2c3e50', marginBottom: '16px', fontWeight: '600' }}>⏰ Smart Wake Alarm</h3>
        
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Target Wake Time:
          </label>
          <input
            type="time"
            value={alarmTime}
            onChange={(e) => setAlarmTime(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              fontSize: '16px',
              border: '1px solid #ddd',
              borderRadius: '8px'
            }}
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Light Sleep Window: ±{smartWindow} minutes
          </label>
          <input
            type="range"
            min="15"
            max="60"
            value={smartWindow}
            onChange={(e) => setSmartWindow(e.target.value)}
            style={{ width: '100%' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
            <span>15 min</span>
            <span>60 min</span>
          </div>
        </div>

        <div style={{ backgroundColor: '#e3f2fd', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
          <div style={{ fontSize: '14px', color: '#1565c0' }}>
            <strong>Smart Wake Range:</strong> {new Date(`2000-01-01 ${alarmTime}`).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', hour12: true})} 
            ± {smartWindow} minutes
          </div>
          <div style={{ fontSize: '12px', color: '#1976d2', marginTop: '4px' }}>
            Wake up during light sleep for a more refreshed feeling
          </div>
        </div>

        <Button 
          onClick={setAlarm} 
          style={{ 
            width: '100%', 
            backgroundColor: alarmSet ? '#4ade80' : '#4361ee',
            fontSize: '16px',
            fontWeight: '600'
          }}
        >
          {alarmSet ? '✅ Alarm Set!' : '⏰ Set Smart Alarm'}
        </Button>
      </Card>

      <div style={{ marginTop: '24px', display: 'flex', gap: '8px' }}>
        <Button onClick={() => navigate('/nutrition')} style={{ flex: 1, backgroundColor: '#4ade80' }}>
          ← Nutrition
        </Button>
        <Button onClick={() => navigate('/social')} style={{ flex: 1, backgroundColor: '#e91e63' }}>
          Social Feed →
        </Button>
      </div>
    </div>
  );
}

export default SleepSense;
